import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Briefcase, Calendar, ExternalLink, Linkedin, Mail, Map, MapPin, Network, Pencil, Phone, Star, Tag, Trash2, UserCog, Users, BadgeCheck, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PersonAvatar } from "@/components/common/Visuals";
import { ConfirmDialog, MembershipBadge } from "@/components/common/Dialogs";
import { EmptyState, ErrorState, LoadingGrid, LoginPrompt } from "@/components/common/States";
import { InfoRow } from "@/components/common/Layout";
import { BackLink, DetailCard, LinkedGroupChips } from "@/components/common/Detail";
import { OrgLinks } from "@/components/orgs/OrgViews";
import { PersonLists } from "@/components/persons/PersonLists";
import { PersonActivity } from "@/components/persons/PersonActivity";
import { formatDate } from "@/lib/format";

export default function PersonDetailPage() {
  const { id } = useParams();
  const { isAuthed, isAdmin } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirm, setConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { data: p, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["person", id, isAuthed],
    queryFn: async () => (await api.get(`/persons/${id}`)).data,
  });

  if (isLoading) return <div className="lm-container py-10"><LoadingGrid count={3} /></div>;
  if (isError) {
    return (
      <div className="lm-container py-10">
        {error.response?.status === 404
          ? <EmptyState title="Contact introuvable" description="Ce contact n'existe pas ou a été supprimé." action={<Button asChild className="rounded-full"><Link to="/personnes">Retour au répertoire</Link></Button>} />
          : <ErrorState onRetry={refetch} />}
      </div>
    );
  }

  const remove = async () => {
    setDeleting(true);
    try {
      await api.delete(`/persons/${id}`);
      toast.success("Contact supprimé (archivé dans Notion)");
      navigate("/personnes");
      queryClient.removeQueries({ queryKey: ["person", id] });
      queryClient.invalidateQueries();
    } catch (err) {
      toast.error(errorMessage(err));
      setDeleting(false);
    }
  };
  const mainOf = p.organizations.filter((o) => p.main_contact_of?.includes(o.id));
  const communities = [...p.clubs, ...p.commissions, ...p.groups];

  return (
    <div className="lm-container pb-20">
      <BackLink to="/personnes" label="Personnes" />
      <section className="lm-card relative overflow-hidden p-6 sm:p-8 lm-fade-up" data-testid="person-detail-header">
        <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-r from-[#A4247F]/12 via-[#E4087E]/6 to-[#EC7F13]/12" />
        <div className="relative flex flex-col gap-6 md:flex-row md:items-end">
          <PersonAvatar person={p} size="xl" className="ring-4 ring-white" />
          <div className="min-w-0 flex-1">
            <h1 className="text-3xl font-extrabold sm:text-4xl" data-testid="person-detail-name">{p.full_name}</h1>
            {p.job_title && <p className="mt-1 text-lg text-stone-600" data-testid="person-detail-job">{p.job_title}</p>}
            <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
              <OrgLinks orgs={p.organizations} />
              {mainOf.length > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-0.5 text-[11px] font-bold text-amber-800" data-testid="person-main-contact-badge">
                  <Star className="h-3 w-3 fill-amber-500 text-amber-500" /> Contact principal
                </span>
              )}
              <MembershipBadge value={p.membership} />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {p.linkedin && (
              <Button asChild variant="outline" className="rounded-full border-[#0A66C2]/30 text-[#0A66C2]" data-testid="person-detail-linkedin">
                <a href={p.linkedin} target="_blank" rel="noreferrer"><Linkedin className="h-4 w-4" /> LinkedIn</a>
              </Button>
            )}
            {isAuthed && (
              <>
                <Button asChild className="rounded-full" data-testid="person-edit-button"><Link to={`/personnes/${id}/modifier`}><Pencil className="h-4 w-4" /> Modifier</Link></Button>
                {p.notion_url && <Button asChild variant="outline" className="rounded-full" data-testid="person-open-notion-button"><a href={p.notion_url} target="_blank" rel="noreferrer"><ExternalLink className="h-4 w-4" /> Ouvrir dans Notion</a></Button>}
              </>
            )}
            {isAdmin && (
              <Button variant="outline" className="rounded-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => setConfirm(true)} data-testid="person-delete-button">
                <Trash2 className="h-4 w-4" /> Supprimer
              </Button>
            )}
          </div>
        </div>
        {communities.length > 0 && <div className="relative mt-6"><LinkedGroupChips groups={communities} /></div>}
      </section>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        {isAuthed ? (
          <>
            <DetailCard title="Coordonnées" icon={Mail} testId="person-contact-card">
              <InfoRow icon={Mail} label="Email" testId="person-detail-email">{p.email && <a href={`mailto:${p.email}`} className="font-semibold text-primary hover:underline">{p.email}</a>}</InfoRow>
              <InfoRow icon={Phone} label="Téléphone" testId="person-detail-phone">{p.phone && <a href={`tel:${p.phone}`} className="hover:underline">{p.phone}</a>}</InfoRow>
              <InfoRow icon={MapPin} label="Ville">{p.city}</InfoRow>
              <InfoRow icon={Map} label="Département">{p.geo_department}</InfoRow>
              {!p.email && !p.phone && !p.city && <p className="text-sm text-muted-foreground">Aucune coordonnée renseignée.</p>}
            </DetailCard>
            <DetailCard title="Profil" icon={UserCog} testId="person-profile-card">
              <InfoRow icon={Briefcase} label="Catégorie métier">{p.business_category}</InfoRow>
              <InfoRow icon={Network} label="Département métier">{p.business_department?.join(", ")}</InfoRow>
              <InfoRow icon={BadgeCheck} label="Adhésion">{p.membership}</InfoRow>
              <InfoRow icon={UserCog} label="Type de profil">{p.profile_type?.join(", ")}</InfoRow>
              <InfoRow icon={Activity} label="Statut">{p.status}</InfoRow>
              <InfoRow icon={Calendar} label="Créé / modifié">{`${formatDate(p.created_time)} · ${formatDate(p.last_edited_time)}`}</InfoRow>
            </DetailCard>
          </>
        ) : (
          <div className="lg:col-span-2">
            <LoginPrompt title="Coordonnées et informations internes" description="Email, téléphone et informations métier sont visibles une fois connecté avec votre adresse @lamelee.com." />
          </div>
        )}
        <DetailCard title="Clubs & Commissions" icon={Users} testId="person-communities-card">
          {[["Clubs", p.clubs], ["Commissions", p.commissions], ["Autres groupes", p.groups]].filter(([label, items]) => label !== "Autres groupes" || items.length).map(([label, items]) => (
            <div key={label} className="py-2.5">
              <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-stone-500">{label}</div>
              <LinkedGroupChips groups={items} empty="Aucun(e)" />
            </div>
          ))}
          <div className="border-t border-border/70 py-2.5" data-testid="person-expertises">
            <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-stone-500">Expertises</div>
            {p.expertises.length ? <div className="flex flex-wrap gap-2">{p.expertises.map((e) => (
              <Link key={e} to={`/personnes?expertise=${encodeURIComponent(e)}`} className="inline-flex items-center gap-1 rounded-full bg-teal-50 px-2.5 py-1 text-xs font-semibold text-teal-700 hover:bg-teal-100"><Tag className="h-3 w-3" /> {e}</Link>
            ))}</div> : <span className="text-sm text-muted-foreground">Aucune</span>}
          </div>
          {isAuthed && (
            <div className="border-t border-border/70 pt-3">
              <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-stone-500">Listes</div>
              <PersonLists person={p} />
            </div>
          )}
        </DetailCard>
      </div>

      {isAuthed && <PersonActivity personId={p.id} />}
      <ConfirmDialog open={confirm} onOpenChange={setConfirm} loading={deleting} onConfirm={remove}
        title={`Supprimer ${p.full_name} ?`}
        description="La fiche sera archivée dans Notion (corbeille) et disparaîtra de l'annuaire. Elle reste récupérable depuis Notion pendant 30 jours." />
    </div>
  );
}
